import { BreakpointTrackerContext } from '@jutro/layout';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _, { update } from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useTranslator } from '@jutro/locale';
import metadata from './TFPAAdditionalInsuredDetails.metadata.json5';
import messages from './TFPAAdditionalInsuredDetails.messages.js';
import styles from './TFPAAdditionalInsuredDetails.module.scss';

function TFPAAdditionalInsuredDetails(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { item, saveData, cancelData, isInvalid, showErrors, onValidate } = props;
    const {
        isComponentValid,
        onValidate: setComponentValidation,
    } = useValidation('TFPAAdditionalInsuredDetails');

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const [dateOfBirthValidationMessage, setDateOfBirthValidationMessage] = useState([]);
    const [additionalInfoVM, setAdditionalInfoVM] = useState(item);
    const { refreshData } = useDataRefresh();
    const [displayError, setDisplayError] = useState(false);
    const errorHandling = useCallback(() => {
        if (!displayError) { setDisplayError(true) }
    }, [displayError]);
    const isIndividual = additionalInfoVM?.accountContactDTO?.value?.subtype === 'Person';
    const isEstateOrTrust = additionalInfoVM?.accountContactDTO?.value?.subtype === 'Company';


    useEffect(() => {
        if (onValidate && !_.isNull(isComponentValid)) {
            onValidate(isComponentValid, 'TFPAAdditionalInsuredDetails');
        }
    }, [isComponentValid, onValidate]);

    useEffect(() => {
        isInvalid(isComponentValid, 'TFPAAdditionalInsuredDetails');
    }, [isInvalid, isComponentValid])

    const saveAdditionalInsured = () => isComponentValid ? saveData(additionalInfoVM) : errorHandling();


    const cancelAdditionalInsured = () => {
        isInvalid(true, 'TFPAAdditionalInsuredDetails');
        cancelData();
    }

    // DE16336
    const validateDateOfBirth = useCallback(
        (dob) => {
            const birthDate = moment(dob);
            const today = moment();
            const age = today.diff(birthDate, 'years');

            if (age < 16 || age > 120) {
                return [translator(messages.ageValidationError)];
            }

            return []; 
        },
        [translator]
    );


    const onDateOfBirthChange = useCallback(
        (value, path) => {
            _.set(additionalInfoVM, path, value);

            const validationMessages = validateDateOfBirth(value);

            if (validationMessages.length > 0) {
                setDateOfBirthValidationMessage(validationMessages);
            } else {
                setDateOfBirthValidationMessage([]);
            }

            setAdditionalInfoVM(additionalInfoVM);
        },
        [additionalInfoVM, validateDateOfBirth]
    );

    const writeValue = useCallback(
        (value, path) => {
            _.set(additionalInfoVM, path, value);
            setAdditionalInfoVM(additionalInfoVM);
            refreshData();
        },
        [additionalInfoVM, refreshData]
    );

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        name: {
            visible: isEstateOrTrust
        },
        firstName: {
            visible: isIndividual
        },
        lastName: {
            visible: isIndividual
        },
        dateOfBirth: {
            visible: isIndividual,
            validationMessages: dateOfBirthValidationMessage
        },
        realationshipToNamedInsured: {
            path: 'relationshipToInsured'
        },
        saveButton: {
            disabled: !isComponentValid,
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onSave: saveAdditionalInsured,
            onCancel: cancelAdditionalInsured,
            onDateOfBirthChange: onDateOfBirthChange
        },
    };


    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={additionalInfoVM}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            onValueChange={writeValue}
            onValidationChange={setComponentValidation}
            showErrors={showErrors || displayError}
        />
    );
}

TFPAAdditionalInsuredDetails.propTypes = {
    item: PropTypes.shape({}).isRequired,
    saveData: PropTypes.func.isRequired
};

export default TFPAAdditionalInsuredDetails;
